import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Lock, Settings, PlusCircle, X } from 'react-feather';
import { Helmet } from 'react-helmet-async';
import Markdown from 'react-markdown';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { Edit as EditIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useQuery } from '@tanstack/react-query';
import type { BrandInsightsPageContext, ChatContextItem } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import type { GeneratedMessage } from '@trinity/chat/types/message-generation';
import {
  ChatInstanceRoot,
  DisclaimerContainer,
  Footer as ChatFooter,
  MessagesWrap as ChatMessagesWrap,
} from '@trinity/chat-components/components/Chat/Chat.js';
import { DispatchPageContextDataContext } from '@trinity/chat-components/components/Chat/context.js';
import { InputWrap } from '@trinity/chat-components/components/Chat/Input.js';
import { SidebarWrapper as DigitalTwinSidebarWrapper } from '@trinity/chat-components/components/DigitalTwin/DigitalTwinChat.styles.js';
import { DigitalTwinChat } from '@trinity/chat-components/components/DigitalTwin/index.js';
import { FileIcon } from '@trinity/chat-components/components/icons/File/index.js';
import { MessageLibrarySidePanel } from '@trinity/chat-components/components/MessageLibrary.SidePanel/index.js';
import { PageRenderer } from '@trinity/chat-components/components/PageDashboard/PageRenderer/index.js';
import { SegmentAvatar } from '@trinity/chat-components/components/SegmentAvatar/index.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import type { SegmentPageBuilderApi } from '@trinity/chat-components/containers/SegmentPageBuilder/index.js';
import {
  SegmentPageBuilderContainer,
  SegmentPageBuilderApiProvider,
} from '@trinity/chat-components/containers/SegmentPageBuilder/index.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';
import { usePage } from '@trinity/chat-components/hooks/usePage.js';
import chatBackgroundImg from '@trinity/chat-components/static/images/chat-background.png';
import { getAuthenticatedClient } from '@/api/client';
import { config } from '@/config';
import { ProjectDetailsContext } from '@/screens/Project.Main/Context';
import { MessageGenerationContainer } from '../MessageGenSession/MessageGeneration.Container';
import { BulkUploadModal } from '../MessageLibrary/BulkUploadModal';
import { MessageEditorModal } from '../MessageLibrary/MessageEditorModal';
import {
  createSegmentRecord,
  getProjectFiles,
  getSegmentByPage,
  getSegmentationRun,
  getSegmentPersonaStatus,
  getSegmentQuoteStatus,
  listSegments,
  patchSegmentMetadata,
  type ProjectFileItem,
  type SegmentationRunRecord,
} from './api/client';
import { ChatInterfaceNativeContainer } from './components/ChatInterfaceNativeContainer';
import {
  ChatToggleBtn,
  ChatRightModule,
  LightbulbIcon,
  ResizeHandle,
  SidebarArea as ChatSidebarArea,
  useChatPanelResize,
} from './components/ChatPanel';
import { MessageGenChatInterface } from './components/MessageGenChatInterface';
import SegmentMarketResearchInsights, {
  type SegmentQuotesData,
} from './components/SegmentMarketResearchInsights';
import SegmentMarketViewPanel, { type CharacteristicsSection } from './components/SegmentMarketViewPanel';
import SegmentOverviewCard from './components/SegmentOverviewCard';
import SegmentQualInsights from './components/SegmentQualInsights';
import { getSegmentAvatar } from './constants/avatars';
import { useForceHackathonBrand } from './hooks/useForceHackathonBrand';
import { SegmentMessageGeneration } from './MessageGeneration';
import type { SegmentProfileTab } from './SegmentProfileContext';
import { SegmentProfileTabContext } from './SegmentProfileContext';
import useSessionStore from './state/sessionStore';
import { buildSegmentDashboard } from './utils/buildSegmentDashboard';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SegmentData {
  id: number;
  name: string;
  size: number;
  pct: number;
  profile_narrative?: string;
  commercial_action?: string;
  gap_analysis?: string | Record<string, unknown>;
  key_stats?: Record<string, Record<string, unknown>>;
  distinguishing_features?: { name: string; z_score: number }[];
  top_molecules?: { molecule: string; mean_share: number }[];
  archetype_distribution?: Record<string, number>;
  momentum_distribution?: Record<string, number>;
  _projectBrand?: string;
  _projectTitle?: string;
}

// ─── Section ordering types ──────────────────────────────────────────────────
type SectionKey =
  | 'overview'
  | 'hcp_attributes'
  | 'characteristics'
  | 'market_research_insights'
  | 'custom_dashboard';
interface SectionOrderItem {
  key: SectionKey;
  hidden: boolean;
}
const DEFAULT_SECTION_ORDER: SectionOrderItem[] = [
  { key: 'overview', hidden: false },
  { key: 'hcp_attributes', hidden: false },
  { key: 'characteristics', hidden: false },
  { key: 'market_research_insights', hidden: false },
  { key: 'custom_dashboard', hidden: false },
];
const SECTION_LABELS: Record<SectionKey, string> = {
  overview: 'Segment Overview',
  hcp_attributes: 'HCP Attributes and Traits',
  characteristics: 'Segment Characteristics',
  market_research_insights: 'Market Research Insights',
  custom_dashboard: 'Custom Dashboard',
};

interface LocationState {
  segment?: SegmentData;
  segmentIndex?: number;
  // Optional fast-path: ExploreSegmentsPage passes the raw DB row for this
  // page through navigation state so this screen can render immediately
  // without re-fetching listSegments just to find the one matching row.
  // The record may be SKINNY (metadata._listView === true) because
  // Explore uses view=list for bulk loading — in that case the hero/
  // narrative still paint from state but we upgrade to the fat row via
  // GET /segments/by-page/:pageId in the background.
  segmentDbRecord?: Record<string, unknown>;
  from?: {
    path?: string;
    label?: string;
  };
}

function inferBackLabelFromPath(path: string | undefined): string {
  if (!path) return 'Back to Segmentation';
  if (path.startsWith('/explore-segments')) return 'Back to Explore Segments';
  if (path.startsWith('/segmentation/cross-segment-dashboard'))
    return 'Back to Cross-Segment Dashboard';
  if (path.startsWith('/segmentation')) return 'Back to Segmentation';
  if (path.startsWith('/segments')) return 'Back to Segments';
  if (/^\/project(s)?\//.test(path)) return 'Back to Project';
  return 'Back';
}

export const SEGMENT_NAMES = [
  'Elderberry Dabbler',
  'Elderberry Dominant',
  'Grape Adopter',
  'Defender of Banana + Fig + Lemon',
  'Doublet Disciple',
] as const;

function basenameOf(p: string): string {
  const cleaned = p.replace(/\\/g, '/');
  const idx = cleaned.lastIndexOf('/');
  return idx >= 0 ? cleaned.slice(idx + 1) : cleaned;
}

function extensionOf(name: string): string {
  const base = basenameOf(name);
  const idx = base.lastIndexOf('.');
  if (idx < 0 || idx === base.length - 1) return '';
  return base.slice(idx + 1).toLowerCase();
}

// True when the record came from the skinny view=list serializer and
// doesn't contain the fat metadata (marketView, qualInsights, quotes,
// full segmentProfile). Marker is set in serializeSegmentListView on the
// chat-api side. Absent marker ⇒ fat row (or nothing at all).
function isSkinnyRecord(record: Record<string, unknown> | null | undefined): boolean {
  if (!record) return false;
  const meta = record.metadata as Record<string, unknown> | undefined;
  return meta?._listView === true;
}

// ─── Migrate Button (temporary) ──────────────────────────────────────────────

function MigrateToDbButton({
  seg,
  pageId,
  pageData,
}: {
  seg: SegmentData | null;
  pageId: number;
  pageData: unknown;
}): React.JSX.Element | null {
  const [migrating, setMigrating] = useState(false);
  const [migrated, setMigrated] = useState(false);

  if (!seg || migrated) return migrated ? <MigrateBadge>Migrated</MigrateBadge> : null;

  const handleMigrate = async (): Promise<void> => {
    setMigrating(true);
    try {
      const stored = pageData as Record<string, unknown> | undefined;
      const providerData = (stored as { providerData?: Record<string, unknown> })?.providerData;
      const segProfile = (providerData?.segmentProfile ?? seg) as Record<string, unknown>;

      const config = buildSegmentDashboard(segProfile as never, 'Segment Insights');

      await createSegmentRecord({
        projectId: 1,
        name: seg.name || `Segment ${pageId}`,
        description: seg.profile_narrative?.slice(0, 500) ?? undefined,
        iconUrl: getSegmentAvatar(pageId % 9),
        ttsSettings: { avatarId: undefined, ttsConfig: null },
        metadata: {
          _hackathon: true,
          _migratedFromPageId: pageId,
          segmentProfile: segProfile,
          subtitle: `${(seg.size ?? 0).toLocaleString()} HCPs (${(((seg.pct as number) ?? 0) * 100).toFixed(0)}%)`,
          attributes: seg.archetype_distribution
            ? Object.entries(seg.archetype_distribution)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 2)
                .map(([k]) => k)
            : undefined,
          sampleData: {
            segmentN: seg.size ?? 0,
            totalN: Math.round((seg.size ?? 0) / ((seg.pct as number) || 1)),
          },
        },
        dashboardConfig: config as unknown as Record<string, unknown>,
      });

      setMigrated(true);
      alert('Migrated to segments DB! Check /segments page.');
    } catch (err) {
      alert(`Migration failed: ${(err as Error)?.message ?? 'Unknown error'}`);
    } finally {
      setMigrating(false);
    }
  };

  return (
    <MigrateBtn onClick={handleMigrate} disabled={migrating}>
      {migrating ? 'Migrating...' : 'Migrate to Segments DB'}
    </MigrateBtn>
  );
}

/**
 * Wrapper component for MessageLibrarySidePanel that handles message clicks and creation.
 */
function MessageLibrarySidePanelWithContext({
  segmentId,
  segmentationSessionId,
  segmentationRunId,
  segmentName,
  onMessageClick,
}: {
  segmentId?: string;
  segmentationSessionId?: string;
  segmentationRunId?: number;
  segmentName?: string;
  onMessageClick: (message: GeneratedMessage) => void;
}) {
  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = React.useState(false);

  return (
    <>
      <MessageLibrarySidePanel
        segmentId={segmentId}
        segmentationSessionId={segmentationSessionId}
        segmentName={segmentName}
        onMessageClick={onMessageClick}
        onAddMessage={() => setCreateModalOpen(true)}
        onBulkUpload={() => setBulkUploadOpen(true)}
      />
      <MessageEditorModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        message={null}
        messageGenerationSessionId={undefined}
        brands={[]}
        enableSegmentSelection={true}
        preSelectedSegmentationRunId={segmentationRunId ?? null}
        preSelectedSegmentId={segmentId ?? null}
      />
      <BulkUploadModal
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        brands={[]}
        enableSegmentSelection={true}
        preSelectedSegmentationRunId={segmentationRunId ?? null}
        preSelectedSegmentId={segmentId ?? null}
      />
    </>
  );
}

const StyledSegmentAvatar = styled(SegmentAvatar)({
  marginRight: 15,
});

const MigrateBtn = styled.button`
  margin-left: auto;
  background: #28aae1;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #1d94c7;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MigrateBadge = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: #4cb847;
  font-weight: 600;
`;

// ─── Styled (matching frontend SegmentDashboards styling) ────────────────────

const Root = styled.div`
  height: calc(100vh - 60px);
  background: url(${chatBackgroundImg}) no-repeat center;
  background-size: cover;
  display: flex;
  gap: 0;
  overflow: hidden;
  padding: 12px;
`;

const LeftModule = styled.div<{ $fillHeight?: boolean; $narrow?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  background: #ffffff;
  overflow-y: ${(p) => (p.$fillHeight ? 'hidden' : 'auto')};
  overflow-x: hidden;
  border-radius: 12px;
  box-shadow: 0 2px 6px 2px rgba(60, 64, 67, 0.15);
  ${(p) => p.$narrow && `max-width: 1280px; margin: 0 auto; width: 100%;`}
`;

const Wrap = styled.div<{ $fillHeight?: boolean; $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${(p) => (p.$fullWidth ? 'none' : '1280px')};
  margin: 0 auto;
  padding: 0 12px;
  ${(p) => p.$fillHeight && 'flex: 1; min-height: 0; overflow: hidden;'}
`;

const Breadcrumbs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0 5px 0;
`;

const BackLink = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #626262;
  font-size: 14px;
  padding: 0;
  cursor: pointer;
  &:hover {
    color: #1271c4;
  }
`;

const ManageBtn = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${(p) => (p.$active ? '#f8faff' : '#ffffff')};
  border: 1px solid ${(p) => (p.$active ? '#1271c4' : '#d3d3d3')};
  border-radius: 4px;
  color: ${(p) => (p.$active ? '#1271c4' : '#323232')};
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  padding: 6px 14px;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
  &:hover {
    background: #f8faff;
    border-color: #1271c4;
    color: #1271c4;
  }
`;

/* Tab bar — single "Overview" tab, active state */
const TabBarWrap = styled.div`
  display: flex;
  position: relative;
  background-color: #ffffff;
  align-items: center;
`;

const TabsContainer = styled.div`
  display: flex;
  position: relative;
  flex: 1;
`;

const ManageIconBtn = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  margin-right: 12px;
  border: none;
  background: transparent;
  color: ${(p) => (p.$active ? '#1271c4' : '#626262')};
  cursor: pointer;
  transition: color 0.15s;
  &:hover {
    color: #1271c4;
  }
`;

const Tab = styled.div<{ $active?: boolean; $clickable?: boolean }>`
  display: inline-flex;
  padding: 0 16px 6px;
  font-size: 16px;
  line-height: 34px;
  min-height: 34px;
  text-align: center;
  justify-content: center;
  font-weight: 700;
  white-space: nowrap;
  color: ${(p) => (p.$active ? '#1271c4' : '#626262')};
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  position: relative;
  &:hover {
    color: ${(p) => (p.$clickable ? '#1271c4' : undefined)};
  }
`;

const TabIndicator = styled.div<{ $left: number; $width: number }>`
  position: absolute;
  bottom: 0;
  left: ${(p) => p.$left}px;
  width: ${(p) => p.$width}px;
  height: 4px;
  background-color: #1271c4;
  transition:
    left 0.15s ease,
    width 0.15s ease;
`;

const TabBarLine = styled.div`
  height: 3px;
  background-color: #e5e5e5;
  width: 100%;
  position: absolute;
  bottom: 1px;
  left: 0;
  right: 0;
`;

/* Profile header card — matches frontend Header styled component */
const StickyHeaderArea = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: #ffffff;
  padding-bottom: 10px;
  padding-top: 12px;
`;

const ProfileCard = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  color: #323232;
  padding: 10px 12px;
  background: #ffffff;
  border-radius: 8px;
  margin-bottom: 5px;
  border: 1px solid #e5e5e5;
  overflow: visible;
`;

const ProfileBackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  color: #8a8a8a;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  align-self: flex-start;
  transition: color 0.15s;
  &:hover {
    color: #1271c4;
  }
`;

const QuoteProcessingCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #f8f9fb;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
  color: #666;
  font-size: 14px;
`;

const QuoteProcessingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #e5e5e5;
  border-top-color: #4f46e5;
  border-radius: 50%;
  flex-shrink: 0;
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/* Avatar — matches SegmentAvatar: 122px ring, 3px border */
const AvatarContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  margin-left: 16px;
  margin-right: 24px;
  margin-bottom: 16px;
`;

const AvatarRing = styled.div`
  width: 122px;
  height: 122px;
  border-radius: 50%;
  border: 3px solid #28aae1;
  padding: 3px;
  background: white;
  box-sizing: border-box;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  display: block;
`;

/* Left section with name, subtitle, tags — matches .leftSection */
const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProfileName = styled.div`
  color: #323232;
  font-size: 20px;
  font-weight: bold;
`;

const ProfileSubtitle = styled.div`
  color: #323232;
  font-size: 13px;
`;

const Tags = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Tag = styled.span`
  color: #323232;
  background-color: #e5f5fc;
  border-radius: 12px;
  padding: 6px 10px;
  font-size: 14px;
`;

/* Sample size box — matches SampleSize styled component */
const SampleSizeBox = styled.div`
  color: #323232;
  border-radius: 8px;
  flex-shrink: 0;
  background-color: #f2f2f2;
  padding: 16px;
  min-width: 280px;
  height: auto;
  font-size: 14px;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const SampleSizeValue = styled.div`
  font-size: 14px;
  font-weight: bold;
`;

const MetaBoxesRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const RightInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  margin-left: auto;
`;

const MetaBox = styled.div`
  color: #323232;
  border-radius: 8px;
  background-color: #f2f2f2;
  padding: 8px 10px;
  min-width: 100px;
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetaBoxLabel = styled.span`
  font-size: 12px;
  color: #626262;
`;

const MetaBoxValue = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: #323232;
`;

const LockedTab = styled.div`
  display: inline-flex;
  padding: 0 16px 6px;
  font-size: 16px;
  line-height: 34px;
  min-height: 34px;
  text-align: center;
  justify-content: center;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: #626262;
  cursor: not-allowed;
  opacity: 0.5;
  white-space: nowrap;
`;

const SyntheticTab = styled(Tab)``;

/* Synthetic Audience tab — hosts the DigitalTwinChat component. The chat
   fills the available vertical space so the user can have a natural
   conversation with the segment's first-person persona. */
const SyntheticAudiencePanel = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 0;
  margin-bottom: 16px;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  /* Hide the chat disclaimer */
  ${DisclaimerContainer} {
    display: none;
  }

  /* Hide the chat sidebar */
  ${DigitalTwinSidebarWrapper} {
    display: none;
  }

  /* The shared Chat component's root subtracts a modal-header offset
     (calc(100% - 55px)) from its height. There's no modal header here,
     so reclaim that space to remove the gap below the chat input. */
  ${ChatInstanceRoot} {
    height: 100%;
  }

  /* The shared Chat caps MessagesWrap and Footer at 1100px wide and
     centers them. In this panel we want them to fill the full width. */
  ${ChatMessagesWrap},
  ${ChatFooter} {
    max-width: none;
  }

  /* Tighten the footer's padding now that the modal-header gap is gone,
     so the input sits flush with the panel. The disclaimer below is
     hidden in this panel (see DisclaimerContainer override above) so no
     bottom padding is needed for it. */
  ${ChatFooter} {
    padding-top: 10px;
    padding-bottom: 10px;
  }

  /* The chat Input has its own 10px bottom margin on its Wrap; zero it
     out so the only space below the input is the panel's own padding. */
  ${InputWrap} {
    margin-bottom: 0;
  }
`;

const ExpandCollapseBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  color: #323232;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  padding: 6px 14px;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
  &:hover {
    background: #f8faff;
    border-color: #1271c4;
    color: #1271c4;
  }
`;

/* Files tab — grid of file cards matching Workspace.FilesTable/Files.Table.Grid */
const FilesPanel = styled.div`
  margin: 8px 36px 16px;
  padding: 20px;
  box-sizing: border-box;
`;

const ManagePanel = styled.div`
  margin: 8px 36px 16px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 24px;
`;

const MessageGeneratorTabContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border: 1px solid #e5e5e5;
  border-bottom: none;
  border-radius: 12px 12px 0 0;
`;

const FilesSection = styled.div`
  & + & {
    margin-top: 28px;
  }
`;

const FilesSectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #626262;
  margin-bottom: 14px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

/* Files tab list-view rows */
const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
`;

const FileRow = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  cursor: pointer;
  box-sizing: border-box;
  border-bottom: 1px solid #f2f2f2;
  transition: background-color 0.12s ease;

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    background-color: #f8faff;
  }

  &:focus-visible {
    outline: 2px solid #1271c4;
    outline-offset: -2px;
  }
`;

const FileRowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
`;

const FileRowDetails = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  flex: 1;
`;

const FileRowName = styled.div`
  color: #323232;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const FileRowSubtitle = styled.div`
  color: #888;
  font-size: 12px;
  margin-top: 2px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const FilesEmpty = styled.div`
  color: #888;
  font-size: 14px;
  font-style: italic;
  padding: 12px 0;
`;

// ─── Manage tab styled components (from SegmentManage.tsx) ──────────────────
const SegmentHeader = styled.div`
  margin-bottom: 32px;
`;

const SegName = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 16px;
  color: #323232;
`;

const DescContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const DescBox = styled.div`
  background: #f1f1f1;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.6;
  color: #323232;
  flex: 1;
  padding: 10px 12px;
`;

const DescInput = styled.textarea`
  padding: 16px;
  font-size: 14px;
  line-height: 1.6;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  color: #323232;
  resize: vertical;
  font-family: inherit;
  flex: 1;
  min-height: 80px;
  outline: none;
  &:focus {
    border-color: #28aae1;
  }
`;

const InputWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const EditActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const IconBtn = styled.div`
  border-radius: 50%;
  border: 2px solid transparent;
  padding: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  transition: all 0.2s;
  &:hover {
    border-color: #28aae1;
    background: #28aae1;
  }
  &:hover svg {
    color: #fff;
  }
  svg {
    color: #28aae1;
    transition: all 0.2s;
  }
`;

const Placeholder = styled.span`
  color: #adadad;
  font-style: italic;
`;

const ProfileSection = styled.div`
  margin-top: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 24px;
  color: #323232;
`;

const Field = styled.div`
  margin-bottom: 24px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #323232;
`;

const FieldInput = styled.input`
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  color: #323232;
  flex: 1;
  outline: none;
  &:focus {
    border-color: #28aae1;
  }
  &::placeholder {
    color: #adadad;
  }
  &[readonly] {
    background: #f1f1f1;
    cursor: default;
  }
`;

const SampleInputs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const AttrWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const AttrRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
`;

const AttrChip = styled.span<{ $editable?: boolean }>`
  height: 32px;
  padding: 0 8px;
  font-size: 14px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  background-color: rgb(229, 245, 252);
  color: var(--black-l);
  ${(p) => p.$editable && 'cursor: pointer;'}
`;

const PersonaSection = styled.div`
  margin-top: 32px;
`;

const PersonaContent = styled.div`
  background: #f1f1f1;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 12px;
  font-size: 14px;
  line-height: 1.6;
  color: #323232;
  flex: 1;
  min-height: 120px;

  p {
    margin: 0 0 8px;
  }

  ul,
  ol {
    margin: 0 0 8px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 4px;
  }
`;

const PersonaTextarea = styled.textarea`
  padding: 12px;
  font-size: 14px;
  line-height: 1.6;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  color: #323232;
  resize: vertical;
  font-family: inherit;
  flex: 1;
  min-height: 120px;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  overflow-y: hidden;
  &:focus {
    border-color: #28aae1;
  }
`;

const ChatConfigSection = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e5e5;
`;

const MessageTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  font-size: 14px;
  line-height: 1.6;
  color: #323232;
  background: #f1f1f1;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  min-height: 80px;
  outline: none;
  &:focus {
    border-color: #28aae1;
  }
  &::placeholder {
    color: #adadad;
  }
`;

const QuestionEditList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const QuestionEditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const QuestionInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 1.6;
  color: #323232;
  background: #f1f1f1;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  font-family: inherit;
  box-sizing: border-box;
  outline: none;
  &:focus {
    border-color: #28aae1;
  }
  &::placeholder {
    color: #adadad;
  }
`;

const QuestionDeleteBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #626262;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.15s ease;
  &:hover {
    opacity: 0.7;
  }
`;

const AddQuestionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background-color: #28aae1;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:hover {
    opacity: 0.85;
  }
`;

const QuestionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const QuestionItem = styled.li`
  padding: 8px 12px;
  background: #f1f1f1;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  font-size: 14px;
  color: #323232;
`;

/* Persona generation status banner (shown while async LLM job is running) */
const PersonaStatusBanner = styled.div<{ $tone: 'info' | 'error' }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin: 8px 0 0;
  border-radius: 8px;
  font-size: 13px;
  border: 1px solid ${(p) => (p.$tone === 'error' ? '#fca5a5' : '#bfdbfe')};
  background: ${(p) => (p.$tone === 'error' ? '#fef2f2' : '#eff6ff')};
  color: ${(p) => (p.$tone === 'error' ? '#991b1b' : '#1e40af')};
`;

const PersonaStatusSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid #bfdbfe;
  border-top-color: #1d4ed8;
  border-radius: 50%;
  flex-shrink: 0;
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/* Overview card — matches .card + .header + .content */
const OverviewCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: transparent;
  border-radius: 8px;
  /* Horizontal margin matches the PageRenderer's RGL containerPadding (36px)
     so the Segment Overview card aligns with the Segment Characteristics /
     Segmentation Model Results blocks rendered below. */
  margin: 8px 36px 16px;
  border: 1px solid #e5e5e5;
  background-color: #ffffff;
`;

const OverviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: #ffffff;
  border-radius: 8px 8px 0 0;
`;

const OverviewTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #323232;
`;

const Divider = styled.div`
  height: 1px;
  min-height: 1px;
  width: 100%;
  background-color: #e5e5e5;
`;

const OverviewContent = styled.div`
  background: transparent;
  padding: 14px 16px;
`;

/* Narrative + action boxes inside the overview content */
const NarrativeText = styled.p`
  font-size: 14px;
  line-height: 1.7;
  color: #626262;
  margin: 0 0 16px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const ActionBox = styled.div<{ $variant: 'action' | 'gap' }>`
  flex: 1;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  color: #4c4c4c;
  line-height: 1.6;
  ${(p) =>
    p.$variant === 'action'
      ? `
    background: #e3f9e8;
    border: 1px solid #4CB847;
  `
      : `
    background: #fffbeb;
    border: 1px solid #f59e0b;
  `}
`;

const ActionLabel = styled.div<{ $variant: 'action' | 'gap' }>`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  color: ${(p) => (p.$variant === 'action' ? '#2d7a2e' : '#92400e')};
`;

/* Page area for the auto-generated dashboard widgets */
const PageArea = styled.div<{ $contained?: boolean }>`
  padding: 0;
  position: relative;
  /* Offset the BuilderCanvas containerPadding so blocks align with sibling cards */
  ${(p) =>
    p.$contained
      ? `margin: 0; width: 100%;`
      : `margin-left: -36px; margin-right: -36px; width: calc(100% + 72px);`}

  /* Unify MUI Accordion headers inside the dashboard renderer (Segment
     Characteristics, Segment Model Results, Custom Dashboard) with the
     three locally-styled headers above. */
  .MuiAccordionSummary-root {
    min-height: 48px;
    padding-left: 16px;
    padding-right: 16px;
  }
  .MuiAccordionSummary-root.Mui-expanded {
    min-height: 48px;
  }
  .MuiAccordionSummary-content,
  .MuiAccordionSummary-content.Mui-expanded {
    margin: 8px 0;
  }
  .MuiAccordionSummary-root .MuiTypography-root {
    font-size: 18px;
    font-weight: 700;
    font-family: inherit;
    color: #323232;
    margin-bottom: 0;
  }
`;

const EditDashboardToggle = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 36px 4px;
`;

const EditDashboardBtn = styled.button`
  border: 1px solid #1976d2;
  border-radius: 6px;
  background: transparent;
  color: #1976d2;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 16px;
  cursor: pointer;
  transition: all 120ms ease;
  &:hover {
    background: #e3f2fd;
  }
`;

/* ─── Edit-mode sub-tabs ──────────────────────────────────────────────────── */
const EditSubTabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e5e5e5;
  margin-bottom: 16px;
`;

const EditSubTab = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid ${(p) => (p.$active ? '#1271c4' : 'transparent')};
  margin-bottom: -2px;
  padding: 8px 18px;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  color: ${(p) => (p.$active ? '#1271c4' : '#626262')};
  cursor: pointer;
  font-family: inherit;
  transition:
    color 0.12s,
    border-color 0.12s;
  &:hover {
    color: #1271c4;
  }
`;

/* ─── Section order manager ───────────────────────────────────────────────── */
const SectionOrderCard = styled.div`
  background: #f8f9fb;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const SectionOrderTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #626262;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const SectionOrderRow = styled.div<{ $dimmed?: boolean; $dragging?: boolean; $dragOver?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  border-radius: 4px;
  opacity: ${(p) => (p.$dimmed ? 0.5 : 1)};
  background: ${(p) => (p.$dragging ? '#e3edf9' : p.$dragOver ? '#f0f4ff' : 'transparent')};
  border: 1px dashed ${(p) => (p.$dragOver ? '#1271c4' : 'transparent')};
  transition:
    background 0.12s,
    border-color 0.12s;
  &:hover {
    background: ${(p) => (p.$dragging ? '#e3edf9' : '#eef2f7')};
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  cursor: grab;
  color: #999;
  &:active {
    cursor: grabbing;
  }
`;

const SectionOrderLabel = styled.span`
  flex: 1;
  font-size: 14px;
  color: #323232;
`;

const VisibilityBtn = styled.button`
  background: none;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  color: #626262;
  display: flex;
  align-items: center;
  border-radius: 3px;
  &:hover {
    background: #dde4ed;
    color: #323232;
  }
`;

export type SegmentProfileScreenProps = {
  /** When set, use this pageId instead of reading from route params.
   *  Lets the project subtab container resolve `:id` → dashboardPageId
   *  and render this screen without duplicating its body. */
  pageIdOverride?: number;
};

function SegmentProfileScreenInner({ pageIdOverride }: SegmentProfileScreenProps = {}): React.JSX.Element {
  useForceHackathonBrand();

  const { pageId: pageIdParam } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const apiClient = useChatApiClient();
  const brandIdentifier = useActiveBrandIdentifier();

  const syntheticPersonaEnabled = true;

  // Resizable chat width management (matches Segment Generator)
  const { chatWidth, rootRef, handleResizeMouseDown } = useChatPanelResize();

  // Auto-resize persona textarea
  const personaTextareaRef = useRef<HTMLTextAreaElement>(null);

  const proxyFetch = useCallback(
    ({ agentId, extensionPath }: { agentId: number; extensionPath: string }) =>
      apiClient.pages.proxyWidgetData(agentId, extensionPath, brandIdentifier ?? undefined),
    [apiClient, brandIdentifier]
  );

  const numericPageId = pageIdOverride ?? (pageIdParam ? +pageIdParam : NaN);

  // Project name from the parent ProjectDetailsContext when rendered inside
  // a project subtab. Falls back to segmentProfile._projectTitle when the
  // screen is mounted from the global segmentation builder route (which
  // doesn't provide the context).
  const projectDetailsCtx = useContext(ProjectDetailsContext);
  const projectNameFromContext = projectDetailsCtx?.query?.data?.project?.name ?? null;
  const { data: pageData } = usePage(isNaN(numericPageId) ? null : numericPageId);
  // Probe-segment chat panel removed — the global ChatWidgetContainer
  // already provides a lightbulb on this route, and the purple duplicate
  // was confusing users. Chat comes from the global widget now.
  const pageAreaRef = useRef<HTMLDivElement>(null);

  // Segment DB record — used for marketView + metadata.quotes.
  //
  // Explore Segments passes a record through navigation state as a fast
  // path so the hero/narrative can paint in the first frame. That record
  // may be SKINNY (metadata._listView === true) because Explore uses
  // view=list for its bulk fetch. When it's skinny we still paint the
  // hero instantly, then upgrade to the fat row via
  // GET /segments/by-page/:pageId in the background so the market view
  // panel and quotes card can hydrate.
  //
  // When no nav state is present (cold URL load / hard refresh), we fetch
  // the fat row by-page directly.
  const initialRecord = state?.segmentDbRecord ?? null;
  const initialIsSkinny = isSkinnyRecord(initialRecord);
  const [segmentDbRecord, setSegmentDbRecord] = useState<Record<string, unknown> | null>(initialRecord);
  // Loading flag only blocks nothing full-page any more — components
  // conditionally render when their slice of metadata arrives. We track
  // it so SegmentMarketViewPanel/Insights can optionally show skeletons.
  const [segmentDbLoading, setSegmentDbLoading] = useState(
    (initialRecord == null || initialIsSkinny) && !isNaN(numericPageId)
  );
  useEffect(() => {
    // Skip network only when we already have the FAT row.
    if (segmentDbRecord && !isSkinnyRecord(segmentDbRecord)) return;
    if (isNaN(numericPageId)) {
      setSegmentDbLoading(false);
      return;
    }
    let cancelled = false;
    setSegmentDbLoading(true);
    getSegmentByPage(numericPageId)
      .then(({ segment }) => {
        if (cancelled || !segment) return;
        setSegmentDbRecord(segment);
      })
      .catch(() => {
        // Fall back to the legacy full-list scan only if the targeted
        // lookup fails entirely (404 / transient error). This preserves
        // the old behavior for cold-load edge cases.
        if (cancelled) return;
        listSegments({ hackathon: true })
          .then(({ segments }) => {
            if (cancelled) return;
            const match = segments.find(
              (s) => (s as Record<string, unknown>).dashboardPageId === numericPageId
            );
            if (match) setSegmentDbRecord(match as Record<string, unknown>);
          })
          .catch(() => {});
      })
      .finally(() => {
        if (!cancelled) setSegmentDbLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // segmentDbRecord is intentionally read only on mount / page change —
    // the guard above prevents refetches once the fat row is in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericPageId]);

  const marketViewData = useMemo(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    return (meta?.marketView as Record<string, unknown> | undefined) ?? null;
  }, [segmentDbRecord]);

  // Tab state for Overview / Synthetic Audience / Files / Message Generator.
  const tabCtx = useContext(SegmentProfileTabContext);

  if (!tabCtx) {
    throw new Error('SegmentProfileTabContext is required for SegmentProfileScreen');
  }

  const { activeTab, setActiveTab, chatOpen, setChatOpen } = tabCtx;

  const tabBarRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    if (!tabBarRef.current) return;
    const activeEl = tabBarRef.current.querySelector<HTMLElement>('[data-tab-active="true"]');
    if (activeEl) {
      setIndicatorStyle({ left: activeEl.offsetLeft, width: activeEl.offsetWidth });
    } else {
      // No tab element active (e.g., manage gear is active) - hide indicator
      setIndicatorStyle({ left: 0, width: 0 });
    }
  }, [activeTab]);

  // segmentation_run_id is the new v2 FK path written by useSaveSegmentPages.
  // When present, the Files tab reads from `run.files` (durable, snapshotted
  // at save time). When absent — old segments, or failed run writes — the
  // tab falls back to listing all files under project 11.
  const segmentationRunId = useMemo<number | undefined>(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    const direct = meta?.segmentationRunId as number | undefined;
    if (typeof direct === 'number') return direct;
    // Legacy segments have no run FK. Return undefined so we fall back.
    return undefined;
  }, [segmentDbRecord]);

  const [runForFiles, setRunForFiles] = useState<SegmentationRunRecord | null>(null);
  const [projectFiles, setProjectFiles] = useState<ProjectFileItem[] | null>(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [filesSource, setFilesSource] = useState<'run' | 'project' | null>(null);
  const [segmentationSessionId, setSegmentationSessionId] = useState<string | null>(null);

  // Hardcoded fallback project id (matches useSaveSegmentPages)
  const FALLBACK_PROJECT_ID = 11;

  const hasSegmentationSession = !!segmentationSessionId;

  useEffect(() => {
    if (activeTab === 'message_generator' || activeTab === 'synthetic') {
      if (hasSegmentationSession) return;
      if (segmentationRunId == null) return;
      getSegmentationRun(segmentationRunId).then(({ run }) => {
        setSegmentationSessionId(run.sessionId ?? null);
      });
    }
  }, [activeTab, hasSegmentationSession, segmentationRunId]);

  useEffect(() => {
    if (activeTab !== 'files') return;
    if (runForFiles !== null || projectFiles !== null) return; // already loaded

    let cancelled = false;
    setFilesLoading(true);
    setFilesError(null);

    const loadFromRun = async (): Promise<boolean> => {
      if (segmentationRunId == null) return false;
      try {
        const { run } = await getSegmentationRun(segmentationRunId);
        if (cancelled) return true;
        setRunForFiles(run);
        setFilesSource('run');
        setSegmentationSessionId(run.sessionId ?? null);
        return true;
      } catch (err) {
        console.warn('[segment-profile] failed to load run for files, falling back to project', err);
        return false;
      }
    };

    const loadFromProject = async (): Promise<void> => {
      try {
        const resp = await getProjectFiles(FALLBACK_PROJECT_ID);
        if (cancelled) return;
        setProjectFiles(resp.items ?? []);
        setFilesSource('project');
      } catch (err) {
        if (cancelled) return;
        setFilesError(err instanceof Error ? err.message : 'Failed to load project files');
        setProjectFiles([]);
      }
    };

    void (async () => {
      const loaded = await loadFromRun();
      if (!loaded && !cancelled) {
        await loadFromProject();
      }
      if (!cancelled) setFilesLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTab, segmentationRunId, runForFiles, projectFiles]);

  const segmentQuotesData = useMemo<SegmentQuotesData | null>(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    const quotes = meta?.quotes as SegmentQuotesData | undefined;
    return quotes ?? null;
  }, [segmentDbRecord]);

  // Derive the async quote-extraction status from segment metadata.
  // 'processing' = queue job is running; 'complete' = quotes available;
  // 'failed' = extraction failed; undefined = not applicable (no qual).
  const quoteStatus = useMemo(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    return meta?._quoteStatus as string | undefined;
  }, [segmentDbRecord]);

  // Poll for quote completion when the segment was saved with async
  // quote extraction. Checks every 5s until status is 'complete' or 'failed'.
  useEffect(() => {
    if (quoteStatus !== 'processing') return;
    const segId = segmentDbRecord?.id as number | undefined;
    if (!segId) return;

    let cancelled = false;
    const poll = () => {
      if (cancelled) return;
      getSegmentQuoteStatus(segId)
        .then(({ quoteStatus: status, quotes }) => {
          if (cancelled) return;
          if (status === 'complete' || status === 'failed') {
            // Patch the local record so quotes render without a full refetch
            setSegmentDbRecord((prev) => {
              if (!prev) return prev;
              const meta: Record<string, unknown> = {
                ...(prev.metadata as Record<string, unknown>),
                _quoteStatus: status,
              };
              if (quotes) meta.quotes = quotes;
              return { ...prev, metadata: meta };
            });
          }
        })
        .catch(() => {
          /* retry on next tick */
        });
    };

    // Initial check after a short delay, then every 5s
    const initialTimer = setTimeout(poll, 2000);
    const interval = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [quoteStatus, segmentDbRecord?.id]);

  // ─── Persona generation polling ──────────────────────────────────────────
  // The persona-generation worker writes `segments_v2.persona` + flips
  // `_personaStatus` on segments_v2.metadata. SegmentProfile reads the
  // legacy `segments` row today, so we poll the v2 sibling directly via
  // GET /segments/v2/:v2Id/persona-status until it resolves. The v2 id is
  // stamped into the legacy metadata at create time by POST /segments.
  const segmentV2IdFromMeta = useMemo<number | undefined>(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    const v = meta?.segmentV2Id as number | undefined;
    return typeof v === 'number' ? v : undefined;
  }, [segmentDbRecord]);

  const [personaStatus, setPersonaStatus] = useState<'pending' | 'generating' | 'complete' | 'failed' | null>(
    null
  );
  const [personaMarkdown, setPersonaMarkdown] = useState<string | null>(null);

  useEffect(() => {
    if (segmentV2IdFromMeta == null) {
      setPersonaStatus(null);
      setPersonaMarkdown(null);
      return;
    }

    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;

    const poll = () => {
      if (cancelled) return;
      getSegmentPersonaStatus(segmentV2IdFromMeta)
        .then(({ personaStatus: status, persona }) => {
          if (cancelled) return;
          setPersonaStatus(status);
          setPersonaMarkdown(persona);
          if (status === 'complete' || status === 'failed' || status === 'pending') {
            if (interval) {
              clearInterval(interval);
              interval = null;
            }
          }
        })
        .catch(() => {
          /* retry on next tick */
        });
    };

    // Initial check, then every 5s while generating
    const initialTimer = setTimeout(poll, 1000);
    interval = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      if (interval) clearInterval(interval);
    };
  }, [segmentV2IdFromMeta]);

  // Blur widget cards whose title matches a known synthetic/preview title so
  // sample charts are visually distinguished from real ones. The list of
  // preview titles lives alongside buildSegmentDashboard so the two stay in
  // sync. Runs after PageRenderer mounts and on every subsequent DOM change
  // inside PageArea.
  // Blur preview widgets in the Segment Characteristics block (old segments only —
  // new saves skip this block entirely via skipCharacteristicsBlock).
  useEffect(() => {
    const container = pageAreaRef.current;
    if (!container) return;
    const applyBlur = (): void => {
      const allTitles = container.querySelectorAll<HTMLElement>('.MuiTypography-root');
      let blockGridItem: HTMLElement | null = null;
      for (const t of Array.from(allTitles)) {
        if ((t.textContent || '').trim() === 'Segment Characteristics') {
          blockGridItem = t.closest<HTMLElement>('.react-grid-item');
          break;
        }
      }
      if (!blockGridItem) return;

      // Blur preview widgets (old segments without market view data).
      const widgetTitles = blockGridItem.querySelectorAll<HTMLElement>('.MuiTypography-root');
      widgetTitles.forEach((titleEl) => {
        if ((titleEl.textContent || '').trim() === 'Segment Characteristics') return;
        const widgetContainer = titleEl.parentElement;
        if (!widgetContainer || widgetContainer.dataset.charBlurred === 'true') return;
        widgetContainer.dataset.charBlurred = 'true';
        widgetContainer.style.position = 'relative';
        Array.from(widgetContainer.children).forEach((child) => {
          if (child === titleEl) return;
          const el = child as HTMLElement;
          el.style.filter = 'blur(6px)';
          el.style.pointerEvents = 'none';
          el.style.userSelect = 'none';
        });
        const overlay = document.createElement('div');
        overlay.style.cssText =
          'position:absolute;inset:0;top:32px;display:flex;align-items:center;justify-content:center;pointer-events:none;z-index:1;';
        overlay.innerHTML = `
          <div style="display:flex;flex-direction:column;align-items:center;gap:6px;background:#ffffff;padding:16px 24px;border-radius:8px;border:1px solid #e5e5e5;box-shadow:0 2px 8px rgba(0,0,0,0.06);">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#626262" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <span style="font-size:12px;color:#626262;font-weight:500;">Insights Pending</span>
          </div>
        `;
        widgetContainer.appendChild(overlay);
      });
    };
    const t = setTimeout(applyBlur, 400);
    const observer = new MutationObserver(() => applyBlur());
    observer.observe(container, { childList: true, subtree: true });
    return () => {
      clearTimeout(t);
      observer.disconnect();
    };
  }, [numericPageId, pageData, marketViewData]);

  // Force-collapse the "Segment Model Results" block on mount
  useEffect(() => {
    const container = pageAreaRef.current;
    if (!container) return;
    const collapseModelResults = (): void => {
      const allTitles = container.querySelectorAll<HTMLElement>('.MuiTypography-root');
      for (const t of Array.from(allTitles)) {
        if (
          (t.textContent || '').trim() === 'Segment Model Results' ||
          (t.textContent || '').trim() === 'Segmentation Model Results'
        ) {
          // Find the MUI Accordion — it has aria-expanded
          const accordion = t.closest<HTMLElement>('.MuiAccordion-root');
          if (accordion) {
            const summary = accordion.querySelector<HTMLElement>('.MuiAccordionSummary-root');
            if (summary?.getAttribute('aria-expanded') === 'true') {
              summary.click();
            }
          }
          break;
        }
      }
    };
    const t = setTimeout(collapseModelResults, 500);
    return () => clearTimeout(t);
  }, [numericPageId, pageData]);

  // ─── Overview inline editing ────────────────────────────────────────────
  const handleOverviewSave = useCallback(
    async (fields: {
      narrative?: string;
      commercialAction?: string;
      gapAnalysis?: string | Record<string, unknown>;
    }) => {
      if (segmentV2IdFromMeta == null) return;
      const { segment } = await patchSegmentMetadata(segmentV2IdFromMeta, {
        segmentProfile: {
          profile_narrative: fields.narrative,
          commercial_action: fields.commercialAction,
          gap_analysis: fields.gapAnalysis,
        },
      });
      if (segment) setSegmentDbRecord(segment as Record<string, unknown>);
    },
    [segmentV2IdFromMeta]
  );

  // ─── Quotes inline editing (hide/restore) ──────────────────────────────
  const handleQuotesSave = useCallback(
    async (updatedQuotes: SegmentQuotesData) => {
      if (segmentV2IdFromMeta == null) return;
      const { segment } = await patchSegmentMetadata(segmentV2IdFromMeta, {
        quotes: updatedQuotes,
      });
      if (segment) setSegmentDbRecord(segment as Record<string, unknown>);
    },
    [segmentV2IdFromMeta]
  );

  // ─── Characteristics section visibility ─────────────────────────────────
  const hiddenCharacteristicsSections = useMemo<CharacteristicsSection[]>(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    return (meta?.hiddenCharacteristicsSections as CharacteristicsSection[] | undefined) ?? [];
  }, [segmentDbRecord]);

  const handleCharacteristicsSave = useCallback(
    async (sections: CharacteristicsSection[]) => {
      if (segmentV2IdFromMeta == null) return;
      const { segment } = await patchSegmentMetadata(segmentV2IdFromMeta, {
        hiddenCharacteristicsSections: sections,
      });
      if (segment) setSegmentDbRecord(segment as Record<string, unknown>);
    },
    [segmentV2IdFromMeta]
  );

  // ─── Global section edit mode ────────────────────────────────────────────
  // When true, per-section pencil icons appear. Toggled by a single pencil
  // button next to the Manage button in the breadcrumbs.
  const [sectionEditMode, setSectionEditMode] = useState(false);

  // Sub-tab inside edit mode: "sections" vs "dashboard"
  const [editSubTab, setEditSubTab] = useState<'sections' | 'dashboard'>('sections');

  // ─── Section ordering ───────────────────────────────────────────────────
  const sectionOrder = useMemo<SectionOrderItem[]>(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    const stored = meta?.sectionOrder as SectionOrderItem[] | undefined;
    if (stored && Array.isArray(stored) && stored.length > 0) return stored;
    return DEFAULT_SECTION_ORDER;
  }, [segmentDbRecord]);

  const handleSectionOrderSave = useCallback(
    async (order: SectionOrderItem[]) => {
      if (segmentV2IdFromMeta == null) return;
      const { segment } = await patchSegmentMetadata(segmentV2IdFromMeta, {
        sectionOrder: order,
      });
      if (segment) setSegmentDbRecord(segment as Record<string, unknown>);
    },
    [segmentV2IdFromMeta]
  );

  // Drag-and-drop reordering state
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const handleDragStart = useCallback((idx: number) => {
    setDragIdx(idx);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  }, []);

  const handleDrop = useCallback(
    (targetIdx: number) => {
      if (dragIdx == null || dragIdx === targetIdx) {
        setDragIdx(null);
        setDragOverIdx(null);
        return;
      }
      const next = [...sectionOrder];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(targetIdx, 0, moved);
      handleSectionOrderSave(next);
      setDragIdx(null);
      setDragOverIdx(null);
    },
    [dragIdx, sectionOrder, handleSectionOrderSave]
  );

  const handleDragEnd = useCallback(() => {
    setDragIdx(null);
    setDragOverIdx(null);
  }, []);

  const toggleSectionHidden = useCallback(
    (idx: number) => {
      const next = [...sectionOrder];
      next[idx] = { ...next[idx], hidden: !next[idx].hidden };
      handleSectionOrderSave(next);
    },
    [sectionOrder, handleSectionOrderSave]
  );

  // ─── Page builder edit mode ─────────────────────────────────────────────
  const [pageBuilderEditing, setPageBuilderEditing] = useState(false);

  const pageBuilderApi = useMemo((): SegmentPageBuilderApi | null => {
    if (segmentV2IdFromMeta == null) return null;
    const segId = segmentV2IdFromMeta;
    return {
      getSegmentPage: async () => {
        const res = await apiClient.authenticatedClient.get(`/segments/${segId}/page`);
        const page = res.data;
        return {
          id: page.id,
          name: page.name,
          config: page.currentVersion?.config ?? page.config ?? {},
        };
      },
      savePageVersion: async (_pId, _sId, data) => {
        const res = await apiClient.authenticatedClient.post(`/segments/${segId}/page/version`, data);
        return res.data;
      },
      revertPageVersion: async (_pId, _sId, versionId) => {
        const res = await apiClient.authenticatedClient.post(`/segments/${segId}/page/revert/${versionId}`);
        return res.data;
      },
    };
  }, [segmentV2IdFromMeta, apiClient]);

  // Segment data sources, in precedence order (lowest → highest):
  //   1. pageData.providerData.segmentProfile  — legacy PageBuilder storage
  //   2. segmentDbRecord.metadata.segmentProfile — FULL shape from the
  //        /segments/by-page upgrade (includes commercial_action,
  //        gap_analysis, archetype_distribution, key_stats, etc.)
  //   3. state.segment                         — whatever Explore passed
  //        through navigation state (may be a SKINNY list-view shape)
  //
  // We merge them together so the hero paints instantly from (3) and fills
  // in with the fat fields from (2) as soon as the by-page upgrade
  // resolves. Source (2) is the authoritative "full row" — without it,
  // commercial_action + gap_analysis go missing whenever Explore navigates
  // through the skinny view=list flow.
  const seg = useMemo(() => {
    const legacyStored = (pageData?.providerData as Record<string, unknown> | undefined)?.segmentProfile as
      | Record<string, unknown>
      | undefined;
    const dbStored = (segmentDbRecord?.metadata as Record<string, unknown> | undefined)?.segmentProfile as
      | Record<string, unknown>
      | undefined;
    const merged = {
      ...(legacyStored ?? {}),
      ...(dbStored ?? {}),
      ...(state?.segment ?? {}),
    };
    return Object.keys(merged).length > 0 ? (merged as unknown as SegmentData) : null;
  }, [state?.segment, pageData, segmentDbRecord]);

  // ─── Wire the global chat widget to this segment's context ──────────────
  // While this screen is mounted, we override the global widget's default
  // chat context with a hackathon-agent context scoped to THIS segment
  // (segmentIdentifier + segmentProfile metadata). When the chat opens it
  // will use the segment's first-person persona via segments_v2.persona
  // (see provider.genai-platform.ts getSegment + buildSegmentPersona on
  // the backend). On unmount, clear the override so subsequent pages
  // fall back to the default (hackathon-agent bare for /projects/11/*).
  const dispatchChatContext = useContext(DispatchPageContextDataContext);
  const sessionId = useSessionStore((s) => s.sessionId);

  // Load the chat contexts list once so we can resolve the hackathon
  // agent. We deliberately use hackathon-agent instead of the frontend's
  // `bi-project` identifier because `bi-project` triggers the chat-api's
  // buildAgentMetadata branch that calls the brand-insights backend —
  // which is not configured on this stack (getBrandInsightsClient throws
  // "BRANDINSIGHTS_API_URL is not configured"). `hackathon-agent` takes
  // the safe no-op branch in buildAgentMetadata.
  const { data: chatAgents } = useQuery<ChatContextItem[]>({
    queryKey: ['chat-contexts', brandIdentifier],
    queryFn: () =>
      apiClient.chat.getChatContexts({ brandIdentifier: brandIdentifier! }).then((res) => res.items),
    enabled: !!brandIdentifier,
    staleTime: 5 * 60 * 1000,
  });

  // Extract initialMessage and questions from segmentDbRecord for chat context
  const initialMessage = useMemo(() => {
    return (segmentDbRecord?.initialMessage as string | null | undefined) ?? null;
  }, [segmentDbRecord]);

  const questions = useMemo(() => {
    return (segmentDbRecord?.questions as string[] | null | undefined) ?? null;
  }, [segmentDbRecord]);

  // Build the segment-scoped chat context for the synthetic audience tab.
  // Uses the synthetic-audience-agent which embodies the segment persona
  // and categorizes responses as validated/synthetic/low-confidence.
  const syntheticAudienceChatContext = useMemo<BrandInsightsPageContext.Agent | null>(() => {
    if (!chatAgents?.length || !seg) return null;
    const syntheticAudienceAgent = chatAgents.find((a) =>
      a.agent?.identifier?.startsWith('synthetic-audience-agent')
    );
    if (!syntheticAudienceAgent) return null;

    const identifier = segmentDbRecord?.identifier as string | undefined;
    return {
      type: ChatContextType.Agent,
      data: syntheticAudienceAgent,
      immutableFilters: identifier
        ? {
            tags: [
              {
                id: 0,
                name: `Segment: ${seg.name}`,
                metadata: {
                  canRemove: false,
                  segmentIdentifier: identifier,
                },
              },
            ],
          }
        : undefined,
      metadata: {
        ...(sessionId ? { sessionId } : {}),
        ...(identifier ? { segmentIdentifier: identifier } : {}),
        ...(segmentationSessionId ? { segmentationSessionId } : {}),
        ...(initialMessage ? { initialMessage } : {}),
        ...(questions ? { questions } : {}),
        segmentProfile: {
          name: seg.name,
          size: seg.size,
          pct: seg.pct,
          profile_narrative: seg.profile_narrative,
          commercial_action: seg.commercial_action,
          archetype_distribution: seg.archetype_distribution,
          momentum_distribution: seg.momentum_distribution,
          distinguishing_features: seg.distinguishing_features,
          top_molecules: seg.top_molecules,
        },
      },
    } as unknown as BrandInsightsPageContext.Agent;
  }, [chatAgents, seg, segmentDbRecord, sessionId, segmentationSessionId, initialMessage, questions]);

  useEffect(() => {
    if (!dispatchChatContext || !syntheticAudienceChatContext) return;
    dispatchChatContext(syntheticAudienceChatContext);

    return () => {
      // Reset on unmount so subsequent non-segment pages on /projects/11/*
      // pick up the bare hackathon-agent default from HackathonChatContextBridge.
      dispatchChatContext(null as unknown as BrandInsightsPageContext.Item);
    };
  }, [dispatchChatContext, syntheticAudienceChatContext]);

  const segIndex = state?.segmentIndex ?? 0;
  // Prefer the DB row's iconUrl (set via SegmentManage) so the header
  // matches what the chat shows. Falls back to the deterministic preset
  // when no icon has been chosen yet.
  const dbIconUrl = (segmentDbRecord?.iconUrl as string | null | undefined) ?? null;
  const avatarUrl = dbIconUrl || getSegmentAvatar(segIndex);

  // NOTE: intentionally no full-page spinner while segmentDbLoading is true.
  // The hero header, narrative, tags, and dashboard all render from data
  // that is already available (location.state + usePage), so blocking the
  // whole screen on the DB record fetch makes the UX feel much slower than
  // it needs to be. Sections that specifically depend on the DB record —
  // SegmentMarketViewPanel, SegmentMarketResearchInsights — render
  // conditionally further down once marketViewData / segmentQuotesData
  // become available.

  if (isNaN(numericPageId)) {
    return (
      <Root ref={rootRef}>
        <LeftModule $narrow>
          <Wrap>
            <Breadcrumbs>
              <BackLink onClick={() => navigate(-1)}>
                <ArrowLeft size={14} /> Back to Segmentation
              </BackLink>
            </Breadcrumbs>
            <div style={{ padding: 40, textAlign: 'center', color: '#ADADAD' }}>
              Invalid page.{' '}
              <button
                onClick={() => navigate(-1)}
                style={{
                  color: '#1271c4',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Return to segmentation
              </button>
            </div>
          </Wrap>
        </LeftModule>
      </Root>
    );
  }

  // Build tags from top archetype/momentum
  const tags: string[] = [];
  if (seg?.archetype_distribution) {
    const top = Object.entries(seg.archetype_distribution).sort((a, b) => b[1] - a[1])[0];
    if (top) tags.push(top[0]);
  }
  if (seg?.momentum_distribution) {
    const top = Object.entries(seg.momentum_distribution).sort((a, b) => b[1] - a[1])[0];
    if (top) tags.push(top[0]);
  }

  const totalHcps = seg?.size ? Math.round(seg.size / (seg.pct || 1)) : undefined;

  const syntheticAudienceTabDisabled = true; // disable until the chat context is fully working and the tab content is ready

  const displayLightbulb = !chatOpen && activeTab !== 'synthetic';

  // State to inject messages into the synthetic audience chat
  const [messageToInject, setMessageToInject] = useState<string | null>(null);

  // Clear messageToInject after it's been passed to DigitalTwinChat
  useEffect(() => {
    if (messageToInject && activeTab === 'synthetic') {
      const timer = setTimeout(() => setMessageToInject(null), 100);
      return () => clearTimeout(timer);
    }
  }, [messageToInject, activeTab]);

  // ─── Manage tab state (from SegmentManage.tsx) ──────────────────────────

  // Edit mode states for manage tab
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingSubtitle, setIsEditingSubtitle] = useState(false);
  const [isEditingSampleData, setIsEditingSampleData] = useState(false);
  const [isEditingAttributes, setIsEditingAttributes] = useState(false);
  const [isEditingPersona, setIsEditingPersona] = useState(false);
  const [isEditingChatConfig, setIsEditingChatConfig] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [editedSubtitle, setEditedSubtitle] = useState('');
  const [editedSegmentN, setEditedSegmentN] = useState('');
  const [editedTotalN, setEditedTotalN] = useState('');
  const [editedAttributes, setEditedAttributes] = useState<string[]>([]);
  const [newAttribute, setNewAttribute] = useState('');
  const [editedPersona, setEditedPersona] = useState('');
  const [editedInitialMessage, setEditedInitialMessage] = useState('');
  const [editedQuestions, setEditedQuestions] = useState<string[]>([]);

  // Auto-resize persona textarea
  useEffect(() => {
    const textarea = personaTextareaRef.current;
    if (textarea && isEditingPersona) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(120, textarea.scrollHeight)}px`;
    }
  }, [editedPersona, isEditingPersona]);

  // Editable fields from segmentDbRecord
  const subtitle = useMemo(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    return (meta?.subtitle as string | undefined) ?? '';
  }, [segmentDbRecord]);

  const attributes = useMemo(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    return (meta?.attributes as string[] | undefined) ?? [];
  }, [segmentDbRecord]);

  const sampleData = useMemo(() => {
    const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
    return (
      (meta?.sampleData as { segmentN: number; totalN: number } | undefined) ?? { segmentN: 0, totalN: 0 }
    );
  }, [segmentDbRecord]);

  const persona = useMemo(() => {
    return (segmentDbRecord?.persona as string | null | undefined) ?? null;
  }, [segmentDbRecord]);

  // ─── Save handlers for manage tab ───────────────────────────────────────
  const saveSegmentMetadata = useCallback(
    async (updates: Record<string, unknown>) => {
      if (!segmentV2IdFromMeta) return;
      const client = getAuthenticatedClient();
      const meta = segmentDbRecord?.metadata as Record<string, unknown> | undefined;
      const full = { ...(meta ?? {}), ...updates };
      await client.put(`/segments/${segmentV2IdFromMeta}`, { metadata: full });
      // Refetch segment
      const res = await client.get(`/segments/${segmentV2IdFromMeta}`);
      if (res.data.segment) setSegmentDbRecord(res.data.segment as Record<string, unknown>);
    },
    [segmentV2IdFromMeta, segmentDbRecord]
  );

  const saveSegmentField = useCallback(
    async (field: string, value: unknown) => {
      if (!segmentV2IdFromMeta) return;
      const client = getAuthenticatedClient();
      await client.put(`/segments/${segmentV2IdFromMeta}`, { [field]: value });
      // Refetch segment
      const res = await client.get(`/segments/${segmentV2IdFromMeta}`);
      if (res.data.segment) setSegmentDbRecord(res.data.segment as Record<string, unknown>);
    },
    [segmentV2IdFromMeta]
  );

  const handleSaveDescription = useCallback(() => {
    saveSegmentField('description', editedDescription);
    setIsEditingDescription(false);
  }, [editedDescription, saveSegmentField]);

  const handleSaveSubtitle = useCallback(() => {
    saveSegmentMetadata({ subtitle: editedSubtitle });
    setIsEditingSubtitle(false);
  }, [editedSubtitle, saveSegmentMetadata]);

  const handleSaveSampleData = useCallback(() => {
    saveSegmentMetadata({
      sampleData: {
        segmentN: parseInt(editedSegmentN) || 0,
        totalN: parseInt(editedTotalN) || 0,
      },
    });
    setIsEditingSampleData(false);
  }, [editedSegmentN, editedTotalN, saveSegmentMetadata]);

  const handleAddAttribute = useCallback(() => {
    if (newAttribute.trim()) {
      setEditedAttributes([...editedAttributes, newAttribute.trim()]);
      setNewAttribute('');
    }
  }, [newAttribute, editedAttributes]);

  const handleSaveAttributes = useCallback(() => {
    saveSegmentMetadata({ attributes: editedAttributes });
    setIsEditingAttributes(false);
  }, [editedAttributes, saveSegmentMetadata]);

  const handleDeleteAttribute = useCallback(
    (i: number) => {
      setEditedAttributes(editedAttributes.filter((_, idx) => idx !== i));
    },
    [editedAttributes]
  );

  const handleSavePersona = useCallback(() => {
    saveSegmentField('persona', editedPersona.trim() || null);
    setIsEditingPersona(false);
  }, [editedPersona, saveSegmentField]);

  const handleSaveChatConfig = useCallback(async () => {
    if (!segmentV2IdFromMeta) return;
    const client = getAuthenticatedClient();
    const trimmedMessage = editedInitialMessage.trim() || null;
    const trimmedQuestions = editedQuestions.map((q) => q.trim()).filter((q) => q.length > 0);
    await client.put(`/segments/${segmentV2IdFromMeta}`, {
      initialMessage: trimmedMessage,
      questions: trimmedQuestions,
    });
    // Refetch segment
    const res = await client.get(`/segments/${segmentV2IdFromMeta}`);
    if (res.data.segment) setSegmentDbRecord(res.data.segment as Record<string, unknown>);
    setIsEditingChatConfig(false);
  }, [segmentV2IdFromMeta, editedInitialMessage, editedQuestions]);

  const handleAddQuestion = useCallback(() => {
    setEditedQuestions([...editedQuestions, '']);
  }, [editedQuestions]);

  const handleQuestionChange = useCallback(
    (index: number, value: string) => {
      const next = [...editedQuestions];
      next[index] = value;
      setEditedQuestions(next);
    },
    [editedQuestions]
  );

  const handleDeleteQuestion = useCallback(
    (index: number) => {
      setEditedQuestions(editedQuestions.filter((_, i) => i !== index));
    },
    [editedQuestions]
  );

  // Show loading state until segment data is available
  const isLoadingSegment = !seg || segmentDbLoading;

  if (isLoadingSegment) {
    return (
      <Root ref={rootRef}>
        <LeftModule $narrow>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <PersonaStatusSpinner style={{ width: '32px', height: '32px' }} />
          </div>
        </LeftModule>
      </Root>
    );
  }

  const tabLabel: Record<SegmentProfileTab, string> = {
    overview: 'Overview',
    message_generator: 'Message Generator',
    synthetic: 'Synthetic Audience',
    files: 'Files',
    manage: 'Manage',
  };
  const helmetTitle = seg?.name
    ? `InsightsEDGE - ${seg.name} - ${tabLabel[activeTab]}`
    : `InsightsEDGE - Segment - ${tabLabel[activeTab]}`;

  return (
    <Root ref={rootRef}>
      <Helmet title={helmetTitle} />
      <LeftModule
        $fillHeight={activeTab === 'message_generator' || activeTab === 'synthetic'}
        $narrow={!chatOpen && activeTab !== 'synthetic'}
      >
        <Wrap
          $fillHeight={activeTab === 'message_generator' || activeTab === 'synthetic'}
          $fullWidth={chatOpen || activeTab === 'synthetic'}
        >
          {/* Profile header card + tab bar — sticky so they remain visible while scrolling.
              The profile card is hidden on the Synthetic Audience tab to maximize chat space. */}
          <StickyHeaderArea>
            {activeTab !== 'synthetic' && (
              <ProfileCard>
                {/* SegmentAvatar includes the "Chat with Segment" ring that
                  jumps to the Synthetic Audience tab — ported from the old
                  /segments page. */}
                <StyledSegmentAvatar
                  iconUrl={avatarUrl}
                  name={seg?.name || 'Segment'}
                  onChatWithSegment={
                    syntheticAudienceTabDisabled ? undefined : () => setActiveTab('synthetic')
                  }
                  size={90}
                />

                <LeftSection>
                  <ProfileBackLink onClick={() => navigate(-1)}>
                    {state?.from?.label ?? inferBackLabelFromPath(state?.from?.path)}
                  </ProfileBackLink>
                  <ProfileName>{seg?.name || `Segment ${segIndex + 1}`}</ProfileName>
                  <ProfileSubtitle>
                    {seg?.size?.toLocaleString() ?? '—'} HCPs ({((seg?.pct || 0) * 100).toFixed(0)}%)
                  </ProfileSubtitle>
                </LeftSection>

                <RightInfoColumn>
                  <MetaBoxesRow>
                    <MetaBox>
                      <MetaBoxLabel>Version</MetaBoxLabel>
                      <MetaBoxValue>v1.0</MetaBoxValue>
                    </MetaBox>
                    <MetaBox>
                      <MetaBoxLabel>Sample Size</MetaBoxLabel>
                      <MetaBoxValue>
                        {seg?.size?.toLocaleString() ?? '—'}
                        {totalHcps ? ` / ${totalHcps.toLocaleString()}` : ''}
                      </MetaBoxValue>
                    </MetaBox>
                    <MetaBox>
                      <MetaBoxLabel>Project</MetaBoxLabel>
                      <MetaBoxValue>
                        {projectNameFromContext ||
                          ((seg as unknown as Record<string, unknown> | null)?._projectTitle as string) ||
                          'Untitled'}
                      </MetaBoxValue>
                    </MetaBox>
                  </MetaBoxesRow>
                  {tags.length > 0 && (
                    <Tags>
                      {tags.map((t) => (
                        <Tag key={t}>{t}</Tag>
                      ))}
                    </Tags>
                  )}
                </RightInfoColumn>
              </ProfileCard>
            )}

            {/* Tab bar */}
            <TabBarWrap ref={tabBarRef}>
              <TabsContainer>
                <Tab
                  data-tab-active={activeTab === 'overview'}
                  $active={activeTab === 'overview'}
                  $clickable
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </Tab>
                <Tab
                  data-tab-active={activeTab === 'message_generator'}
                  $active={activeTab === 'message_generator'}
                  $clickable
                  onClick={() => setActiveTab('message_generator')}
                >
                  Message Generator
                </Tab>
                {syntheticPersonaEnabled ? (
                  <SyntheticTab
                    data-tab-active={activeTab === 'synthetic'}
                    $active={activeTab === 'synthetic'}
                    $clickable
                    onClick={() => setActiveTab('synthetic')}
                  >
                    Synthetic Audience
                  </SyntheticTab>
                ) : (
                  <LockedTab>
                    <Lock size={24} /> Synthetic Audience
                  </LockedTab>
                )}
                <Tab
                  data-tab-active={activeTab === 'files'}
                  $active={activeTab === 'files'}
                  $clickable
                  onClick={() => setActiveTab('files')}
                >
                  Files
                </Tab>
                <TabBarLine />
                <TabIndicator $left={indicatorStyle.left} $width={indicatorStyle.width} />
              </TabsContainer>
              {segmentV2IdFromMeta && (
                <ManageIconBtn
                  $active={activeTab === 'manage'}
                  onClick={() => setActiveTab(activeTab === 'manage' ? 'overview' : 'manage')}
                  title="Manage Segment"
                >
                  <Settings size={20} />
                </ManageIconBtn>
              )}
            </TabBarWrap>
          </StickyHeaderArea>

          {/* Persona generation status — shown only while the async LLM job
             is running or just after it completes / fails. Hidden on
             Synthetic Audience tab and once the segment has a stable persona. */}
          {activeTab !== 'synthetic' && personaStatus === 'generating' && (
            <PersonaStatusBanner $tone="info">
              <PersonaStatusSpinner />
              <span>
                Generating chat persona for this segment… The digital-twin chat will use this voice once it's
                ready.
              </span>
            </PersonaStatusBanner>
          )}
          {activeTab !== 'synthetic' && personaStatus === 'failed' && (
            <PersonaStatusBanner $tone="error">
              <span>Persona generation failed. Chat will fall back to a generic voice until you retry.</span>
            </PersonaStatusBanner>
          )}

          {activeTab === 'manage' && (
            <ManagePanel>
              <SegmentHeader>
                <SegName>{seg?.name || 'Segment'}</SegName>
                <DescContainer>
                  {isEditingDescription ? (
                    <InputWithIcon>
                      <DescInput
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        rows={3}
                      />
                      <EditActions>
                        <IconBtn onClick={handleSaveDescription}>
                          <CheckIcon sx={{ fontSize: 16 }} />
                        </IconBtn>
                        <IconBtn onClick={() => setIsEditingDescription(false)}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconBtn>
                      </EditActions>
                    </InputWithIcon>
                  ) : (
                    <InputWithIcon>
                      <DescBox>
                        {(segmentDbRecord?.description as string | null | undefined) || (
                          <Placeholder>Add a description</Placeholder>
                        )}
                      </DescBox>
                      <IconBtn
                        onClick={() => {
                          setEditedDescription(
                            (segmentDbRecord?.description as string | null | undefined) ?? ''
                          );
                          setIsEditingDescription(true);
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconBtn>
                    </InputWithIcon>
                  )}
                </DescContainer>
              </SegmentHeader>

              <ProfileSection>
                <SectionTitle>Profile</SectionTitle>

                <Field>
                  <FieldLabel>Subtitle</FieldLabel>
                  <InputWithIcon>
                    <FieldInput
                      value={isEditingSubtitle ? editedSubtitle : subtitle}
                      onChange={(e) => setEditedSubtitle(e.target.value)}
                      readOnly={!isEditingSubtitle}
                      placeholder="Enter subtitle"
                    />
                    {!isEditingSubtitle ? (
                      <IconBtn
                        onClick={() => {
                          setEditedSubtitle(subtitle);
                          setIsEditingSubtitle(true);
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconBtn>
                    ) : (
                      <EditActions>
                        <IconBtn onClick={handleSaveSubtitle}>
                          <CheckIcon sx={{ fontSize: 16 }} />
                        </IconBtn>
                        <IconBtn onClick={() => setIsEditingSubtitle(false)}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconBtn>
                      </EditActions>
                    )}
                  </InputWithIcon>
                </Field>

                <Field>
                  <FieldLabel>Attributes</FieldLabel>
                  {isEditingAttributes && (
                    <InputWithIcon>
                      <FieldInput
                        value={newAttribute}
                        onChange={(e) => setNewAttribute(e.target.value)}
                        placeholder="Enter attribute"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleAddAttribute();
                        }}
                      />
                      <EditActions>
                        <IconBtn
                          onClick={() => {
                            if (newAttribute.trim()) handleAddAttribute();
                            else handleSaveAttributes();
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 16 }} />
                        </IconBtn>
                        <IconBtn
                          onClick={() => {
                            setEditedAttributes([...attributes]);
                            setNewAttribute('');
                            setIsEditingAttributes(false);
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconBtn>
                      </EditActions>
                    </InputWithIcon>
                  )}
                  <AttrWithIcon>
                    <AttrRow>
                      {(isEditingAttributes ? editedAttributes : attributes).map((attr, i) => (
                        <AttrChip
                          key={i}
                          onClick={isEditingAttributes ? () => handleDeleteAttribute(i) : undefined}
                          $editable={isEditingAttributes}
                        >
                          {attr} {isEditingAttributes && <CloseIcon sx={{ fontSize: 12, ml: 0.5 }} />}
                        </AttrChip>
                      ))}
                      {!isEditingAttributes && attributes.length === 0 && (
                        <Placeholder>Add attributes</Placeholder>
                      )}
                    </AttrRow>
                    {!isEditingAttributes && (
                      <IconBtn
                        onClick={() => {
                          setEditedAttributes([...attributes]);
                          setNewAttribute('');
                          setIsEditingAttributes(true);
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconBtn>
                    )}
                  </AttrWithIcon>
                </Field>

                <Field>
                  <FieldLabel>Sample Data</FieldLabel>
                  <InputWithIcon style={{ alignItems: 'flex-start' }}>
                    <SampleInputs>
                      <FieldInput
                        type="number"
                        value={isEditingSampleData ? editedSegmentN : sampleData.segmentN.toString()}
                        onChange={(e) => setEditedSegmentN(e.target.value)}
                        readOnly={!isEditingSampleData}
                        placeholder="Segment N"
                        min="0"
                      />
                      <FieldInput
                        type="number"
                        value={isEditingSampleData ? editedTotalN : sampleData.totalN.toString()}
                        onChange={(e) => setEditedTotalN(e.target.value)}
                        readOnly={!isEditingSampleData}
                        placeholder="Total N"
                        min="0"
                      />
                    </SampleInputs>
                    {!isEditingSampleData ? (
                      <IconBtn
                        onClick={() => {
                          setEditedSegmentN(sampleData.segmentN.toString());
                          setEditedTotalN(sampleData.totalN.toString());
                          setIsEditingSampleData(true);
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconBtn>
                    ) : (
                      <EditActions>
                        <IconBtn onClick={handleSaveSampleData}>
                          <CheckIcon sx={{ fontSize: 16 }} />
                        </IconBtn>
                        <IconBtn onClick={() => setIsEditingSampleData(false)}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconBtn>
                      </EditActions>
                    )}
                  </InputWithIcon>
                </Field>
              </ProfileSection>

              {/* Segment Persona */}
              <PersonaSection>
                <SectionTitle>Segment Persona</SectionTitle>
                <Field>
                  <FieldLabel>Type or Paste Persona</FieldLabel>
                  {isEditingPersona ? (
                    <InputWithIcon style={{ alignItems: 'flex-start' }}>
                      <PersonaTextarea
                        ref={personaTextareaRef}
                        value={editedPersona}
                        onChange={(e) => setEditedPersona(e.target.value)}
                        placeholder="Paste or type your persona details here...&#10;Example:&#10;&#10;• Voice: Professional, friendly, and supportive&#10;• Behaviors: Responds with empathy, provides actionable advice&#10;• Data: Use customer purchase history and preferences"
                      />
                      <EditActions>
                        <IconBtn onClick={handleSavePersona}>
                          <CheckIcon sx={{ fontSize: 16 }} />
                        </IconBtn>
                        <IconBtn onClick={() => setIsEditingPersona(false)}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconBtn>
                      </EditActions>
                    </InputWithIcon>
                  ) : (
                    <InputWithIcon style={{ alignItems: 'flex-start' }}>
                      <PersonaContent>
                        {persona ? (
                          <Markdown>{persona}</Markdown>
                        ) : (
                          <Placeholder>Add a persona for this segment</Placeholder>
                        )}
                      </PersonaContent>
                      <IconBtn
                        onClick={() => {
                          setEditedPersona(persona ?? '');
                          setIsEditingPersona(true);
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconBtn>
                    </InputWithIcon>
                  )}
                </Field>
              </PersonaSection>

              {/* Chat Config */}
              <ChatConfigSection>
                <SectionTitle>Customize Chat Questions</SectionTitle>
                {isEditingChatConfig ? (
                  <>
                    <Field>
                      <FieldLabel>Initial Message</FieldLabel>
                      <MessageTextarea
                        value={editedInitialMessage}
                        onChange={(e) => setEditedInitialMessage(e.target.value)}
                        placeholder="Enter the initial message shown when the chat opens..."
                        rows={3}
                      />
                    </Field>

                    <Field>
                      <InputWithIcon style={{ alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <FieldLabel>Questions</FieldLabel>
                          <QuestionEditList>
                            {editedQuestions.map((q, i) => (
                              <QuestionEditRow key={i}>
                                <QuestionInput
                                  type="text"
                                  value={q}
                                  onChange={(e) => handleQuestionChange(i, e.target.value)}
                                  placeholder="Enter a question..."
                                />
                                <QuestionDeleteBtn
                                  type="button"
                                  onClick={() => handleDeleteQuestion(i)}
                                  aria-label={`Delete question ${i + 1}`}
                                >
                                  <X size={18} />
                                </QuestionDeleteBtn>
                              </QuestionEditRow>
                            ))}
                          </QuestionEditList>
                          <AddQuestionBtn type="button" onClick={handleAddQuestion}>
                            <PlusCircle size={14} />
                            Add Question
                          </AddQuestionBtn>
                        </div>
                        <EditActions>
                          <IconBtn onClick={handleSaveChatConfig}>
                            <CheckIcon sx={{ fontSize: 16 }} />
                          </IconBtn>
                          <IconBtn onClick={() => setIsEditingChatConfig(false)}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconBtn>
                        </EditActions>
                      </InputWithIcon>
                    </Field>
                  </>
                ) : (
                  <>
                    <Field>
                      <InputWithIcon style={{ alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <FieldLabel>Initial Message</FieldLabel>
                          <PersonaContent style={{ minHeight: 'auto' }}>
                            {initialMessage || ''}
                          </PersonaContent>
                        </div>
                        <IconBtn
                          onClick={() => {
                            setEditedInitialMessage(initialMessage ?? '');
                            setEditedQuestions(questions ?? []);
                            setIsEditingChatConfig(true);
                          }}
                        >
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconBtn>
                      </InputWithIcon>
                    </Field>

                    <Field>
                      <FieldLabel>Questions</FieldLabel>
                      {questions && questions.length > 0 ? (
                        <QuestionsList>
                          {questions.map((q, i) => (
                            <QuestionItem key={i}>{q}</QuestionItem>
                          ))}
                        </QuestionsList>
                      ) : (
                        <Placeholder>No questions configured</Placeholder>
                      )}
                    </Field>
                  </>
                )}
              </ChatConfigSection>
            </ManagePanel>
          )}

          {activeTab === 'overview' && (
            <>
              {/* ──── Edit mode: sub-tabs ──────────────────────────────── */}
              {sectionEditMode ? (
                <>
                  <EditSubTabBar>
                    <EditSubTab
                      $active={editSubTab === 'sections'}
                      onClick={() => {
                        setEditSubTab('sections');
                        setPageBuilderEditing(false);
                      }}
                    >
                      Sections
                    </EditSubTab>
                    <EditSubTab
                      $active={editSubTab === 'dashboard'}
                      onClick={() => {
                        setEditSubTab('dashboard');
                        setPageBuilderEditing(true);
                      }}
                    >
                      Custom Dashboard
                    </EditSubTab>
                  </EditSubTabBar>

                  {editSubTab === 'sections' && (
                    <>
                      {/* Section order manager */}
                      <SectionOrderCard>
                        <SectionOrderTitle>Section Order</SectionOrderTitle>
                        {sectionOrder.map((item, idx) => (
                          <SectionOrderRow
                            key={item.key}
                            $dimmed={item.hidden}
                            $dragging={dragIdx === idx}
                            $dragOver={dragOverIdx === idx}
                            draggable
                            onDragStart={() => handleDragStart(idx)}
                            onDragOver={(e) => handleDragOver(e, idx)}
                            onDrop={() => handleDrop(idx)}
                            onDragEnd={handleDragEnd}
                          >
                            <DragHandle>
                              <DragIndicatorIcon fontSize="small" />
                            </DragHandle>
                            <SectionOrderLabel>{SECTION_LABELS[item.key]}</SectionOrderLabel>
                            <VisibilityBtn
                              onClick={() => toggleSectionHidden(idx)}
                              title={item.hidden ? 'Show section' : 'Hide section'}
                            >
                              {item.hidden ? (
                                <VisibilityOffIcon fontSize="small" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </VisibilityBtn>
                          </SectionOrderRow>
                        ))}
                      </SectionOrderCard>

                      {/* Render auto-generated sections in order */}
                      {sectionOrder
                        .filter((s) => !s.hidden && s.key !== 'custom_dashboard')
                        .map((s) => {
                          switch (s.key) {
                            case 'overview':
                              return marketViewData ? (
                                <div key={s.key}>
                                  <SegmentOverviewCard
                                    narrative={seg?.profile_narrative}
                                    commercialAction={seg?.commercial_action}
                                    gapAnalysis={seg?.gap_analysis}
                                    editMode
                                    onSave={segmentV2IdFromMeta != null ? handleOverviewSave : undefined}
                                  />
                                  <div style={{ height: 12 }} />
                                </div>
                              ) : null;
                            case 'hcp_attributes':
                              return segmentDbRecord ? (
                                <SegmentQualInsights
                                  key={s.key}
                                  segmentData={segmentDbRecord}
                                  segmentName={seg?.name || ''}
                                  editMode
                                />
                              ) : null;
                            case 'characteristics':
                              return marketViewData ? (
                                <div key={s.key}>
                                  <SegmentMarketViewPanel
                                    data={marketViewData}
                                    segmentName={seg?.name}
                                    hiddenSections={hiddenCharacteristicsSections}
                                    editMode
                                    onSave={
                                      segmentV2IdFromMeta != null ? handleCharacteristicsSave : undefined
                                    }
                                  />
                                </div>
                              ) : null;
                            case 'market_research_insights':
                              return segmentQuotesData ? (
                                <SegmentMarketResearchInsights
                                  key={s.key}
                                  quotes={segmentQuotesData}
                                  segmentName={seg?.name}
                                  editMode
                                  onSave={segmentV2IdFromMeta != null ? handleQuotesSave : undefined}
                                />
                              ) : null;
                            default:
                              return null;
                          }
                        })}
                      {!segmentQuotesData && quoteStatus === 'processing' && (
                        <QuoteProcessingCard>
                          <QuoteProcessingSpinner />
                          <span>
                            Extracting HCP quotes for this segment… This typically takes around 1 minute to
                            complete.
                          </span>
                        </QuoteProcessingCard>
                      )}
                    </>
                  )}

                  {editSubTab === 'dashboard' && (
                    <PageArea ref={pageAreaRef} $contained>
                      {pageBuilderApi && segmentV2IdFromMeta != null ? (
                        <SegmentPageBuilderApiProvider value={pageBuilderApi}>
                          <SegmentPageBuilderContainer
                            projectId={
                              (segmentDbRecord?.projectId as number | undefined) ?? FALLBACK_PROJECT_ID
                            }
                            segmentId={segmentV2IdFromMeta}
                            title="Custom Dashboard"
                          />
                        </SegmentPageBuilderApiProvider>
                      ) : (
                        <PageRenderer
                          pageId={numericPageId}
                          proxyFetch={proxyFetch}
                          brandIdentifier={brandIdentifier ?? undefined}
                        />
                      )}
                    </PageArea>
                  )}
                </>
              ) : (
                /* ──── View mode: render sections in stored order ─────── */
                <>
                  {sectionOrder
                    .filter((s) => !s.hidden)
                    .map((s) => {
                      switch (s.key) {
                        case 'overview':
                          return marketViewData ? (
                            <div key={s.key}>
                              <SegmentOverviewCard
                                narrative={seg?.profile_narrative}
                                commercialAction={seg?.commercial_action}
                                gapAnalysis={seg?.gap_analysis}
                                editMode={false}
                              />
                              <div style={{ height: 12 }} />
                            </div>
                          ) : null;
                        case 'hcp_attributes':
                          return segmentDbRecord ? (
                            <SegmentQualInsights
                              key={s.key}
                              segmentData={segmentDbRecord}
                              segmentName={seg?.name || ''}
                              editMode={false}
                            />
                          ) : null;
                        case 'characteristics':
                          return marketViewData ? (
                            <div key={s.key}>
                              <SegmentMarketViewPanel
                                data={marketViewData}
                                segmentName={seg?.name}
                                hiddenSections={hiddenCharacteristicsSections}
                                editMode={false}
                              />
                            </div>
                          ) : null;
                        case 'market_research_insights':
                          return segmentQuotesData ? (
                            <SegmentMarketResearchInsights
                              key={s.key}
                              quotes={segmentQuotesData}
                              segmentName={seg?.name}
                              editMode={false}
                            />
                          ) : !segmentQuotesData && quoteStatus === 'processing' ? (
                            <QuoteProcessingCard key={s.key}>
                              <QuoteProcessingSpinner />
                              <span>
                                Extracting HCP quotes for this segment… This typically takes around 1 minute
                                to complete.
                              </span>
                            </QuoteProcessingCard>
                          ) : null;
                        case 'custom_dashboard':
                          return (
                            <PageArea key={s.key} ref={pageAreaRef}>
                              <PageRenderer
                                pageId={numericPageId}
                                proxyFetch={proxyFetch}
                                brandIdentifier={brandIdentifier ?? undefined}
                              />
                            </PageArea>
                          );
                        default:
                          return null;
                      }
                    })}
                </>
              )}
            </>
          )}
          {activeTab === 'synthetic' && (
            <SyntheticAudiencePanel>
              {(() => {
                const ttsSettings =
                  (segmentDbRecord?.ttsSettings as
                    | { avatarId?: string | null; ttsConfig?: unknown }
                    | null
                    | undefined) ?? null;
                return (
                  <DigitalTwinChat
                    hideHeader
                    avatarId={ttsSettings?.avatarId ?? undefined}
                    // Pass the segment-scoped chat context so the backend
                    // hints endpoint resolves the segment, returns its
                    // initialMessage as the welcome, maps its questions[]
                    // to starter hints, and the chat agent embodies the
                    // segment's persona via buildSegmentPersona.
                    chatPageContext={syntheticAudienceChatContext as never}
                    ttsConfig={(ttsSettings?.ttsConfig ?? null) as never}
                    disableVideoMode
                    segmentIconUrl={avatarUrl}
                    initialInput={messageToInject ?? undefined}
                  />
                );
              })()}
            </SyntheticAudiencePanel>
          )}
          {activeTab === 'files' && (
            <FilesPanel>
              {filesLoading && <FilesEmpty>Loading files…</FilesEmpty>}
              {!filesLoading && filesError && <FilesEmpty>Could not load files: {filesError}</FilesEmpty>}

              {/* ── Primary: run.files snapshot ────────────────────────────── */}
              {!filesLoading && !filesError && filesSource === 'run' && runForFiles && (
                <>
                  <FilesSection>
                    <FilesSectionTitle>
                      Source Data
                      {runForFiles.files.filter((f) => f.kind === 'quant').length > 0
                        ? ` (${runForFiles.files.filter((f) => f.kind === 'quant').length})`
                        : ''}
                    </FilesSectionTitle>
                    {(() => {
                      const quant = runForFiles.files.filter((f) => f.kind === 'quant');
                      if (quant.length === 0) {
                        return <FilesEmpty>No source dataset recorded for this run.</FilesEmpty>;
                      }
                      return (
                        <FilesList>
                          {quant.map((f) => (
                            <FileRow
                              key={f.id}
                              type="button"
                              title={f.filename}
                              onClick={() => {
                                if (f.workspaceFileId != null) {
                                  navigate(`/workspaces/files/${f.workspaceFileId}`);
                                }
                              }}
                              style={f.workspaceFileId == null ? { cursor: 'default' } : undefined}
                            >
                              <FileRowIcon>
                                <FileIcon
                                  extension={f.extension || extensionOf(f.filename) || 'xlsx'}
                                  size={28}
                                />
                              </FileRowIcon>
                              <FileRowDetails>
                                <FileRowName>{f.filename}</FileRowName>
                                <FileRowSubtitle>Quant input</FileRowSubtitle>
                              </FileRowDetails>
                            </FileRow>
                          ))}
                        </FilesList>
                      );
                    })()}
                  </FilesSection>

                  <FilesSection>
                    <FilesSectionTitle>
                      Qualitative Transcripts
                      {(() => {
                        const allQual = runForFiles.files.filter((f) => f.kind === 'qual');
                        const segNameLower = seg?.name?.toLowerCase();
                        const shouldFilter =
                          segNameLower &&
                          (SEGMENT_NAMES as readonly string[]).some((n) => n.toLowerCase() === segNameLower);
                        const filtered = shouldFilter
                          ? allQual.filter((f) => f.filename.toLowerCase().includes(segNameLower))
                          : allQual;
                        return filtered.length > 0 ? ` (${filtered.length})` : '';
                      })()}
                    </FilesSectionTitle>
                    {(() => {
                      const allQual = runForFiles.files.filter((f) => f.kind === 'qual');
                      const segNameLower = seg?.name?.toLowerCase();
                      const shouldFilter =
                        segNameLower &&
                        (SEGMENT_NAMES as readonly string[]).some((n) => n.toLowerCase() === segNameLower);
                      const qual = shouldFilter
                        ? allQual.filter((f) => f.filename.toLowerCase().includes(segNameLower))
                        : allQual;
                      if (qual.length === 0) {
                        return (
                          <FilesEmpty>No qualitative transcripts were selected for this run.</FilesEmpty>
                        );
                      }
                      return (
                        <FilesList>
                          {qual.map((f) => (
                            <FileRow
                              key={f.id}
                              type="button"
                              title={f.filename}
                              onClick={() => {
                                if (f.workspaceFileId != null) {
                                  navigate(`/workspaces/files/${f.workspaceFileId}`);
                                }
                              }}
                              style={f.workspaceFileId == null ? { cursor: 'default' } : undefined}
                            >
                              <FileRowIcon>
                                <FileIcon
                                  extension={f.extension || extensionOf(f.filename) || 'txt'}
                                  size={28}
                                />
                              </FileRowIcon>
                              <FileRowDetails>
                                <FileRowName>{basenameOf(f.filename)}</FileRowName>
                                <FileRowSubtitle>Transcript</FileRowSubtitle>
                              </FileRowDetails>
                            </FileRow>
                          ))}
                        </FilesList>
                      );
                    })()}
                  </FilesSection>
                </>
              )}

              {/* ── Fallback: project files library ─────────────────────────── */}
              {!filesLoading && !filesError && filesSource === 'project' && (
                <FilesSection>
                  <FilesSectionTitle>
                    Project Library
                    {projectFiles && projectFiles.length > 0 ? ` (${projectFiles.length})` : ''}
                  </FilesSectionTitle>
                  {!projectFiles || projectFiles.length === 0 ? (
                    <FilesEmpty>No files in this project.</FilesEmpty>
                  ) : (
                    <>
                      <FilesEmpty>
                        This segment predates durable file tracking — showing all files from the project as a
                        fallback.
                      </FilesEmpty>
                      <FilesList>
                        {projectFiles.map((item, idx) => {
                          const f = item.file;
                          const name = f?.name ?? `File ${idx + 1}`;
                          const ext = f?.extension ?? extensionOf(name) ?? '';
                          return (
                            <FileRow
                              key={f?.id ?? `${name}-${idx}`}
                              type="button"
                              title={name}
                              onClick={() => {
                                if (f?.id != null) {
                                  navigate(`/workspaces/files/${f.id}`);
                                }
                              }}
                              style={f?.id == null ? { cursor: 'default' } : undefined}
                            >
                              <FileRowIcon>
                                <FileIcon extension={ext || 'txt'} size={28} />
                              </FileRowIcon>
                              <FileRowDetails>
                                <FileRowName>{basenameOf(name)}</FileRowName>
                                <FileRowSubtitle>Project file</FileRowSubtitle>
                              </FileRowDetails>
                            </FileRow>
                          );
                        })}
                      </FilesList>
                    </>
                  )}
                </FilesSection>
              )}
            </FilesPanel>
          )}
          {activeTab === 'message_generator' && segmentationSessionId && (
            <MessageGeneratorTabContainer>
              <SegmentMessageGeneration
                segmentId={segmentDbRecord?.identifier as string | undefined}
                segmentationSessionId={segmentationSessionId}
              />
            </MessageGeneratorTabContainer>
          )}
        </Wrap>
      </LeftModule>

      {/* Show message library on synthetic tab, or regular chat on other tabs */}
      {(activeTab === 'synthetic' || chatOpen) && <ResizeHandle onMouseDown={handleResizeMouseDown} />}
      {activeTab === 'synthetic' && (
        <ChatRightModule $chatWidth={chatWidth}>
          <ChatSidebarArea>
            <MessageLibrarySidePanelWithContext
              segmentId={segmentDbRecord?.identifier as string | undefined}
              segmentationSessionId={segmentationSessionId ?? undefined}
              segmentationRunId={segmentationRunId}
              segmentName={seg?.name}
              onMessageClick={(message) => {
                const messageContent = message.title ? `${message.title}\n\n${message.body}` : message.body;
                const fullMessage = `How would you react to a statement like this:\n\n${messageContent}`;
                setMessageToInject(fullMessage);
              }}
            />
          </ChatSidebarArea>
        </ChatRightModule>
      )}
      {chatOpen && activeTab !== 'synthetic' && (
        <ChatRightModule $chatWidth={chatWidth}>
          <ChatSidebarArea>
            {activeTab === 'message_generator' ? (
              <MessageGenChatInterface
                segmentIdentifier={segmentDbRecord?.identifier as string | undefined}
                segmentationRunId={segmentationRunId}
              />
            ) : (
              <ChatInterfaceNativeContainer
                onClose={() => setChatOpen(false)}
                segmentIdentifier={segmentDbRecord?.identifier as string | undefined}
                segmentationRunId={segmentationRunId}
              />
            )}
          </ChatSidebarArea>
        </ChatRightModule>
      )}

      {displayLightbulb && (
        <ChatToggleBtn onClick={() => setChatOpen(true)} title="Discover Insights">
          <LightbulbIcon size={24} />
        </ChatToggleBtn>
      )}
    </Root>
  );
}

const VALID_TABS: ReadonlySet<SegmentProfileTab> = new Set([
  'overview',
  'synthetic',
  'files',
  'message_generator',
  'manage',
]);

function parseTabFromSearch(search: string): SegmentProfileTab {
  const raw = new URLSearchParams(search).get('tab');
  return raw && VALID_TABS.has(raw as SegmentProfileTab)
    ? (raw as SegmentProfileTab)
    : 'overview';
}

export function SegmentProfileScreen(props: { pageIdOverride?: number }): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();

  const activeTab = parseTabFromSearch(location.search);
  const [chatOpen, setChatOpen] = useState(false);

  const setActiveTab = useCallback(
    (tab: SegmentProfileTab) => {
      const params = new URLSearchParams(location.search);
      if (tab === 'overview') {
        params.delete('tab');
      } else {
        params.set('tab', tab);
      }
      const qs = params.toString();
      navigate(
        { pathname: location.pathname, search: qs ? `?${qs}` : '' },
        { replace: false, state: location.state }
      );
    },
    [navigate, location.pathname, location.search, location.state]
  );

  const resolvedChatOpen = chatOpen || activeTab === 'message_generator';

  const ctx = useMemo(
    () => ({
      activeTab,
      setActiveTab,
      chatOpen: resolvedChatOpen,
      setChatOpen,
    }),
    [activeTab, setActiveTab, resolvedChatOpen, setChatOpen]
  );

  return (
    <SegmentProfileTabContext.Provider value={ctx}>
      <MessageGenerationContainer>
        <SegmentProfileScreenInner {...props} />
      </MessageGenerationContainer>
    </SegmentProfileTabContext.Provider>
  );
}
